import React, { useState, useEffect } from "react";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog } from "@mui/material";
import MainBox from "../../../../container/MainBox";
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import EmptyState from "../../../../../../component/EmptyState";
import { FilterSelect, StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { useSearchParams } from "react-router-dom";
import { getAllEncashments, getEncashmentById, reviewEncashment } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { money } from "../payrollUtils";

const TONE = { pending: "warning", approved: "success", rejected: "danger" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const nameOf = (e) => e.employee ? `${e.employee.fname || ""} ${e.employee.lname || ""}`.trim() : "—";

const EncashmentQueue = () => {
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null); // row being reviewed
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true); setError(false);
    try {
      const res = await getAllEncashments(status);
      if (res?.success === false) throw new Error(res?.message || "Failed to load encashments");
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load encashments"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [status]);

  // deep-link: ?id=<encashment> opens that request's detail directly
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id) return;
    (async () => {
      try {
        const res = await getEncashmentById(id);
        if (res?.data) { setActive(res.data); setComment(""); }
      } catch { /* ignore */ }
    })();
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeDialog = () => {
    setActive(null);
    if (searchParams.get("id")) { searchParams.delete("id"); setSearchParams(searchParams, { replace: true }); }
  };

  const review = async (decision) => {
    setBusy(true);
    try {
      const res = await reviewEncashment(active._id, { status: decision, comment: comment.trim() });
      if (res?.success === false) throw new Error(res?.message || "Could not update");
      notify.success(`Encashment ${decision}${decision === "approved" ? " — leave balance updated" : ""}`);
      closeDialog(); setComment(""); fetchData();
    } catch (e) { notify.error(e?.message || "Could not update encashment"); }
    finally { setBusy(false); }
  };

  return (
    <Box sx={{ width: "100%" }}>
    <TopBar MainHeading={"Leave Encashment"} />
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Leave Encashments</Box>
        <FilterSelect value={status} onChange={(e) => setStatus(e.target.value)}
          options={[{ value: "", label: "All statuses" }, { value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }]} />
      </Stack>

      {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: "22%" }}>Employee</TableCell>
              <TableCell sx={headCellSx}>Leave</TableCell>
              <TableCell sx={headCellSx} align="right">Days</TableCell>
              <TableCell sx={headCellSx} align="right">Amount</TableCell>
              <TableCell sx={headCellSx}>Period</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
            <TableBody><TableSkeleton columns={6} /></TableBody>
          </Table>
        ) : error ? (
        <EmptyState variant="error" title="Couldn't load encashments" onRetry={fetchData} />
      ) : rows.length === 0 ? (
        <EmptyState title="No encashment requests" description="Employee requests to cash out unused paid leave appear here." />
      ) : (
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: "22%" }}>Employee</TableCell>
              <TableCell sx={headCellSx}>Leave</TableCell>
              <TableCell sx={headCellSx} align="right">Days</TableCell>
              <TableCell sx={headCellSx} align="right">Amount</TableCell>
              <TableCell sx={headCellSx}>Period</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e._id} hover onClick={() => { setActive(e); setComment(""); }} sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{nameOf(e)}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{e.leaveType?.name || "—"}</TableCell>
                <TableCell sx={bodyCellSx} align="right">{e.days}</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{money(e.amount)}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{e.month != null ? `${MONTHS[e.month]} ${e.year}` : "—"}</TableCell>
                <TableCell sx={bodyCellSx}><StatusChip label={e.status} tone={TONE[e.status]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(active)} onClose={() => closeDialog()} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "30rem", maxWidth: "94vw" } }}>
        {active && (() => {
          const avail = active.availableBalance;
          const projected = avail != null ? avail - active.days : null;
          const insufficient = projected != null && projected < 0;
          const Row = ({ label, value, strong }) => (
            <Stack direction="row" justifyContent="space-between" sx={{ py: "0.25rem", fontSize: "0.875rem" }}>
              <Box sx={{ color: tokens.textMuted }}>{label}</Box>
              <Box sx={{ fontWeight: strong ? 700 : 500, color: tokens.text }}>{value}</Box>
            </Stack>
          );
          return (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: "1rem" }}>
                <Box>
                  <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>{nameOf(active)}</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{active.employee?.aliasId || ""}{active.month != null ? ` · pay in ${MONTHS[active.month]} ${active.year}` : ""}</Box>
                </Box>
                <StatusChip label={active.status} tone={TONE[active.status]} />
              </Stack>

              {/* calculation breakdown */}
              <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: "10px", p: "1rem 1.25rem", mb: "1rem" }}>
                <Row label="Leave type" value={active.leaveType?.name || "—"} />
                <Row label="Days to encash" value={`${active.days} day(s)`} />
                <Row label={`Per-day rate (${active.basis} ÷ 30)`} value={money(active.perDay)} />
                <Box sx={{ borderTop: `1px solid ${tokens.border}`, mt: "0.25rem", pt: "0.25rem" }}>
                  <Row label="Encashment amount" value={money(active.amount)} strong />
                </Box>
              </Box>

              {/* balance impact */}
              <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: "10px", p: "1rem 1.25rem", mb: "1rem" }}>
                {active.status === "approved" ? (
                  <Row label="Leave balance" value={`${active.balanceBefore} → ${active.balanceAfter} day(s)`} strong />
                ) : (
                  <>
                    <Row label="Available now" value={avail != null ? `${avail} day(s)` : "—"} />
                    <Row label="After encashment" value={projected != null ? `${projected} day(s)` : "—"} strong />
                    {insufficient && <Box sx={{ fontSize: "0.8125rem", color: tokens.danger, mt: "0.25rem" }}>⚠ Exceeds available balance — cannot approve.</Box>}
                  </>
                )}
              </Box>

              {active.reason && <Box sx={{ fontSize: "0.875rem", mb: "1rem" }}><b>Reason:</b> {active.reason}</Box>}
              {active.reviewerComment && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem" }}><b>Reviewer note:</b> {active.reviewerComment}</Box>}

              {active.status === "pending" ? (
                <>
                  <FormField type="text" value={comment} onChange={(e) => setComment(e.target.value)} fullwidth placeholder="Comment (optional)" />
                  <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1.5rem" }}>
                    <BtnSecondary onClick={() => review("rejected")} disabled={busy} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder }}>Reject</BtnSecondary>
                    <BtnPrimary onClick={() => review("approved")} disabled={busy || insufficient} sx={{ borderRadius: "8px" }}>{busy ? "…" : "Approve"}</BtnPrimary>
                  </Stack>
                </>
              ) : (
                <Stack direction="row" justifyContent="flex-end"><BtnSecondary onClick={() => closeDialog()} sx={{ borderRadius: "8px" }}>Close</BtnSecondary></Stack>
              )}
            </>
          );
        })()}
      </Dialog>
    </MainBox>
    </Box>
  );
};

export default EncashmentQueue;
